/**
 * IAB Global Privacy Platform (GPP) data, read via the IAB CMP API.
 *
 * GPP-compliant CMPs (including OneTrust's `otGPP.js`) expose a global
 * `window.__gpp(command, callback, parameter?)` function per the IAB spec
 * (https://github.com/InteractiveAdvertisingBureau/Global-Privacy-Platform).
 * Calling `__gpp('ping', cb)` returns the current CMP/signal status plus the
 * encoded GPP string and applicable section IDs. The callback is invoked
 * synchronously when the CMP is ready, which is the only state in which we
 * care about the data — anything else returns `null` and lets callers skip
 * consent params.
 */

// The IAB GPP CMP defines this callback signature; the payload varies by command.
// oxlint-disable-next-line anti-slop/no-unknown-parameters
type GppCallback = (data: unknown, success: boolean) => void;
// The IAB GPP CMP defines this signature; `parameter` varies by command.
// oxlint-disable-next-line anti-slop/no-unknown-parameters
type GppFunction = (command: string, callback: GppCallback, parameter?: unknown) => void;

declare global {
  interface Window {
    __gpp?: GppFunction;
  }
}

export interface GppData {
  gppString: string;
  sectionIds: number[];
}

export function getGppData(): GppData | null {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof window === 'undefined' || typeof window.__gpp !== 'function') return null;

  let result: GppData | null = null;
  window.__gpp('ping', (data, success) => {
    if (!success) return;
    result = parseGppPing(data);
  });
  return result;
}

// Decodes an arbitrary value at its boundary and returns a narrowed result.
// oxlint-disable-next-line anti-slop/no-unknown-parameters
function parseGppPing(data: unknown): GppData | null {
  // Narrows an unparsed value before reading it; the parse happens here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof data !== 'object' || data === null) return null;
  if (!('signalStatus' in data) || data.signalStatus !== 'ready') return null;
  // Narrows an unparsed value to a primitive before using it.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (!('gppString' in data) || typeof data.gppString !== 'string' || data.gppString === '') return null;
  if (!('applicableSections' in data) || !Array.isArray(data.applicableSections)) return null;
  // Drop the IAB sentinel -1 ("no section applies") and any non-numeric entries.
  // Real section IDs are positive integers (TCF EU=2, US National=7, US state IDs 8+).
  // Narrows an unparsed value to a primitive before using it.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  const sectionIds = data.applicableSections.filter((n): n is number => typeof n === 'number' && n > 0);
  if (sectionIds.length === 0) return null;
  return { gppString: data.gppString, sectionIds };
}
